// src/components/admin/LoyaltyAIAgent.jsx
// "Loyalty AI" page under Loyalty Management: the admin types an instruction ("Create a Christmas promotion, 10% off,
// for all customers") and the agent carries it out. Anything that gives customers money-off or changes what points
// are worth comes back as an approval card that only runs when the admin presses Approve.
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { LoyaltyAgentService } from '../../api/backendAiService';
import DailyTokenCounter from './DailyTokenCounter';
import '../../styles/bookings-ai-agent.css';

const EXAMPLES = [
  'Create a promotion',
  'Help me create a Christmas promotion campaign that will drive the highest conversions based on past campaign results.',
  'Analyse my previous promotions and suggest improvements for better engagement.',
  "What's the best promotion type to increase customer visit frequency?",
  'Create a birthday promotion campaign for customers born in December.',
  'Which promotions are running right now, and how many vouchers have been redeemed?',
  'Suggest a promotion idea for our slow Tuesday mornings.',
];

const APPROVAL_RULES = [
  'Pushing vouchers to customers',
  'Deleting a promotion',
  'Any change to the loyalty programme (earn rate, point value, tiers, expiry, bonus points)',
  'Editing, pausing or re-activating a promotion customers already hold vouchers for',
  'Discounts over 50%, over $100 off, or points vouchers over $100',
  'More than 5 changes from one instruction',
];

// Same subject / body the Promotions page uses when it starts a campaign for a promotion
const EMAIL_SUBJECTS = {
  percentage: (p) => `🎉 ${p.discountValue}% OFF Just For You - ${p.name}!`,
  fixed_amount: (p) => `💰 Save $${p.discountValue} with ${p.name}!`,
  free_gift: (p) => `🎁 Free Gift with ${p.name}!`,
  points_based: (p) => `⭐ Redeem ${p.pointsRequired} Points for $${p.voucherAmount} - ${p.name}!`,
};
const PUSH_SUBJECTS = {
  percentage: (p) => `🔥 ${p.discountValue}% OFF Flash Sale!`,
  fixed_amount: (p) => `💸 Save $${p.discountValue} Today Only!`,
  free_gift: () => '🎁 Free Gift Alert!',
  points_based: () => '⭐ Points Redemption Available!',
};

function campaignData(promotion, type) {
  const subjects = type === 'email' ? EMAIL_SUBJECTS : PUSH_SUBJECTS;
  const subject = (subjects[promotion.type] || subjects.percentage)(promotion);
  const body = type === 'email'
    ? `Dear Valued Customer,\n\n🌟 We're excited to share an exclusive offer on our ${promotion.name}!\n\n${promotion.description || 'Indulge in our premium beauty services with this limited-time offer.'}\n\n${promotion.voucherCode ? `Use voucher code ${promotion.voucherCode} at your next visit.\n\n` : ''}Book your appointment today!\n\nWith love,\nYour Beauty Team 💄`
    : `${promotion.name} - Limited time offer! Tap to book your appointment! 🌟`;
  return {
    type,
    subject,
    body,
    targetingType: promotion.targetingType,
    targetingCriteria: promotion.targetingCriteria,
    promotionSource: {
      promotionName: promotion.name,
      promotionType: promotion.type,
      promotionId: promotion.promotionId,
      voucherCode: promotion.voucherCode
    }
  };
}

function LoyaltyAIAgent({ salonId, onOpenPromotions, onOpenLoyaltyProgram, onCreateCampaign }) {
  const [messages, setMessages] = useState([{
    id: 'welcome',
    type: 'ai',
    content: "Tell me what you'd like done in Loyalty Management - for example creating a promotion and giving it to a group of customers, pausing or editing a promotion, or changing how customers earn points. I can also recommend promotions based on how your past campaigns performed and on your sales and customer data, then set up the one you pick. I follow the same rules as the Promotions and Loyalty Program pages, and anything that gives customers money-off or changes what points are worth waits for your approval. You don't need to give every detail up front - I'll ask for whatever is missing.",
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  // actionId -> { state: 'pending' | 'working' | 'done' | 'declined' | 'failed', message, promotion }
  const [approvals, setApprovals] = useState({});
  const serviceRef = useRef(null);
  const tokenCounterRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Approve/decline outcomes the agent hasn't been told about yet; sent along with the next instruction
  const unreportedOutcomes = useRef([]);

  useEffect(() => {
    if (salonId) serviceRef.current = new LoyaltyAgentService(salonId);
  }, [salonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading]);

  const addMessage = (msg) => setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), ...msg }]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached || !serviceRef.current) return;

    addMessage({ type: 'user', content: text });
    setInput('');
    setIsLoading(true);

    const outcomes = unreportedOutcomes.current;
    unreportedOutcomes.current = [];
    const outgoing = outcomes.length
      ? `[Update since your last reply: ${outcomes.join(' ')}]\n\n${text}`
      : text;

    try {
      const res = await serviceRef.current.sendMessage(outgoing);
      setApprovals(prev => {
        const next = { ...prev };
        res.pendingActions.forEach(p => { next[p.actionId] = { state: 'pending' }; });
        return next;
      });
      addMessage({ type: 'ai', content: res.message, actions: res.actions, pendingActions: res.pendingActions });
      if (res.dailyUsage) tokenCounterRef.current?.update(res.dailyUsage);
    } catch (error) {
      console.error('Loyalty AI agent error:', error);
      unreportedOutcomes.current = [...outcomes, ...unreportedOutcomes.current];
      const content = error?.status === 429
        ? 'You have reached the daily AI usage limit. Loyalty AI is available again after midnight.'
        : error?.status === 403
          ? (error.message || 'Loyalty AI is not available for your account.')
          : "Something went wrong and I couldn't finish. Please check the Promotions page before trying again - some changes may already have been made.";
      addMessage({ type: 'system', content });
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  const handleDecision = async (action, decision) => {
    setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'working' } }));
    try {
      const res = await serviceRef.current.resolveAction(action.actionId, decision);
      if (res.declined) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'declined', message: 'Declined - nothing was changed.' } }));
        unreportedOutcomes.current.push(`The admin DECLINED "${action.title}", so it was not done.`);
      } else if (res.success) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'done', message: res.message, promotion: res.promotion } }));
        unreportedOutcomes.current.push(`The admin APPROVED "${action.title}" and it was completed: ${res.message}`);
      } else {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: res.message } }));
        unreportedOutcomes.current.push(`The admin approved "${action.title}" but it failed: ${res.message}`);
      }
    } catch (error) {
      setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: error.message } }));
    }
  };

  const startCampaign = (promotion, type) => {
    sessionStorage.setItem('pendingNotificationData', JSON.stringify(campaignData(promotion, type)));
    onCreateCampaign?.('communications', type);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Follow-up links under a completed change: open the page it happened on, or announce the promotion
  const renderLinks = (items) => {
    const promotion = [...items].reverse().find(a => a.promotion)?.promotion;
    const programChanged = items.some(a => a.type === 'update_loyalty_program');
    const promotionChanged = items.some(a => a.type !== 'update_loyalty_program');
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
        {promotionChanged && onOpenPromotions && <button className="bai-link" onClick={onOpenPromotions}>Open Promotions →</button>}
        {programChanged && onOpenLoyaltyProgram && <button className="bai-link" onClick={onOpenLoyaltyProgram}>Open Loyalty Program →</button>}
        {promotion && onCreateCampaign && (
          <>
            <button className="bai-link" onClick={() => startCampaign(promotion, 'email')}>Announce by e-mail →</button>
            <button className="bai-link" onClick={() => startCampaign(promotion, 'push')}>Announce by push →</button>
          </>
        )}
      </div>
    );
  };

  const renderApprovalCard = (p) => {
    const status = approvals[p.actionId] || { state: 'pending' };
    return (
      <div key={p.actionId} className={`bai-approval-card bai-approval-card--${status.state}`}>
        <div className="bai-approval-head">
          <span className="bai-approval-badge">Needs your approval</span>
          <strong>{p.title}</strong>
        </div>
        {p.lines?.map((line, i) => <div key={i} className="bai-approval-line">{line}</div>)}
        <div className="bai-approval-reason">{p.reason}</div>
        {(status.state === 'pending' || status.state === 'working') && (
          <div className="bai-approval-actions">
            <button className="bai-btn bai-btn--approve" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'approve')}>
              {status.state === 'working' ? 'Working…' : 'Approve'}
            </button>
            <button className="bai-btn bai-btn--decline" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'decline')}>
              Decline
            </button>
          </div>
        )}
        {status.state === 'done' && (
          <>
            <div className="bai-approval-result bai-approval-result--ok">✓ {status.message}</div>
            {renderLinks([{ type: p.type, promotion: status.promotion }])}
          </>
        )}
        {status.state === 'declined' && <div className="bai-approval-result">{status.message}</div>}
        {status.state === 'failed' && <div className="bai-approval-result bai-approval-result--err">Not done: {status.message}</div>}
      </div>
    );
  };

  return (
    <div className="ai-tab-container bookings-ai-agent loyalty-ai-agent">
      <div className="ai-tab-header">
        <h2>Loyalty AI</h2>
        <p>Type an instruction and the assistant will carry it out in Loyalty Management - creating and targeting promotions, distributing vouchers and adjusting the loyalty programme, just like doing it by hand. Ask for recommendations too: it analyses your past promotions and sales to suggest effective campaigns.</p>
      </div>

      <DailyTokenCounter ref={tokenCounterRef} salonId={salonId} onLimitReached={setLimitReached} />

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try an instruction</h3>
          <div className="example-prompts">
            {EXAMPLES.map(ex => (
              <div key={ex} className="prompt-card" onClick={() => setInput(ex)}>"{ex}"</div>
            ))}
          </div>
          <div className="bai-rules">
            <h4>Always asks for your approval</h4>
            <ul>{APPROVAL_RULES.map(r => <li key={r}>{r}</li>)}</ul>
            <p>Individual points balances, transactions and cash top-ups are not handled here - use the customer's page.</p>
          </div>
        </div>

        <div className="ai-chat-interface">
          <div className="chat-messages">
            {messages.map(m => (
              <div key={m.id} className={`message ${m.type}-message`}>
                {m.type === 'ai' && <div className="ai-avatar">🤖</div>}
                {m.type === 'user' && <div className="user-avatar">👤</div>}
                {m.type === 'system' && <div className="system-avatar">ℹ️</div>}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  {m.actions?.length > 0 && (
                    <div className="bai-done-list">
                      {m.actions.map((a, i) => (
                        <div key={i} className="bai-done-item">
                          <span className="bai-done-check">✓</span>
                          <span>{a.message}</span>
                        </div>
                      ))}
                      {renderLinks(m.actions)}
                    </div>
                  )}
                  {m.pendingActions?.map(renderApprovalCard)}
                  <div className="message-time">{formatTime(m.timestamp)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at midnight.' : 'e.g. Create a Christmas promotion, 10% off, for all customers (Enter to send, Shift+Enter for a new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />
            <div className="input-actions">
              <span className="bai-hint">Changes appear on the Promotions and Loyalty Program pages immediately.</span>
              <button className="send-btn" onClick={handleSend} disabled={isLoading || !input.trim() || limitReached}>
                {isLoading ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoyaltyAIAgent;
